"use client";

import { c as _c } from "react/compiler-runtime";
import { Activity, ArrowRight, ExternalLink, Gauge, MapPin, Plus, Search, ShieldCheck, Zap } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useMemo, useState } from "react";
import { Button } from "@/lib/components/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/lib/components/card";
import { Input } from "@/lib/components/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/lib/components/select";
import { useApi } from "@/lib/fetch-client";
import { formatCompact, gateProviderStats, hasEnoughRequestsForStats, MIN_REQUESTS_FOR_STATS, type ProviderWindowStats } from "@/lib/provider-stats";
import { activeModelCounts, publicProviderDefinitions } from "@/lib/providers-catalog";
import { countryCodeToFlag, getProviderCountries, isProviderCompliant, type ProviderCompliancePolicy, type ProviderDataPolicy, type ProviderId } from "@llmgateway/models";
import { CarrierMark, providerLogoUrls, svgDataUrlAspectRatio } from "@llmgateway/shared/components";
type SortKey = "fastest" | "slowest" | "popular" | "name" | "uptime";
const getProviderLogo = (providerId: ProviderId, uploadedLogo?: string) => {
  // A carrier-uploaded logo (Airside claim) wins over the built-in mark.
  if (uploadedLogo) {
    // Wordmarks keep their proportions instead of shrinking into the square
    // slot built-in marks use.
    const aspectRatio = svgDataUrlAspectRatio(uploadedLogo) ?? 1;
    const width = Math.min(Math.max(aspectRatio, 1), 3.5) * 48;
    return <div className="flex h-12 min-w-12 shrink items-center overflow-hidden" style={{
      width
    }}>
				<CarrierMark src={uploadedLogo} className="h-12 w-full object-contain" />
			</div>;
  }
  const LogoComponent = providerLogoUrls[providerId];
  if (LogoComponent) {
    return <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden">
				<LogoComponent className="max-h-12 max-w-12 object-contain" />
			</div>;
  }
  return <div className="size-12 shrink-0 rounded-lg bg-muted" />;
};
const PROVIDER_COUNTRIES = getProviderCountries();
const PROVIDER_COUNTRY_CODES = new Set(PROVIDER_COUNTRIES.map(c => c.code));
const badgeClassName = "inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-xs text-muted-foreground";
function CountryBadge(t0) {
  const $ = _c(15);
  const {
    code
  } = t0;
  let t1;
  if ($[0] !== code) {
    t1 = countryCodeToFlag(code);
    $[0] = code;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <span className="leading-none">{t1}</span>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <MapPin className="h-3 w-3" />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== code || $[6] !== t2) {
    t4 = <>{t2}{t3}{code}</>;
    $[5] = code;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const content = t4;
  if (!PROVIDER_COUNTRY_CODES.has(code)) {
    let t5;
    if ($[8] !== content) {
      t5 = <span className={badgeClassName}>{content}</span>;
      $[8] = content;
      $[9] = t5;
    } else {
      t5 = $[9];
    }
    return t5;
  }
  let t5;
  if ($[10] !== code) {
    t5 = code.toLowerCase();
    $[10] = code;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = `/providers/country/${t5}`;
  let t7;
  if ($[12] !== content || $[13] !== t6) {
    t7 = <Link href={t6} onClick={_temp} className={`${badgeClassName} transition-colors hover:border-primary/40 hover:text-primary`}>{content}</Link>;
    $[12] = content;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp(e) {
  return e.stopPropagation();
}
type ComplianceFilterKey = "requireSoc2" | "requireIso27001" | "requireGdpr" | "blockApiTraining" | "zeroDataRetention";
const COMPLIANCE_FILTERS: {
  key: ComplianceFilterKey;
  label: string;
}[] = [{
  key: "requireSoc2",
  label: "SOC 2"
}, {
  key: "requireIso27001",
  label: "ISO 27001"
}, {
  key: "requireGdpr",
  label: "GDPR"
}, {
  key: "blockApiTraining",
  label: "No training"
}, {
  key: "zeroDataRetention",
  label: "ZDR"
}];
function formatTtft(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) {
    return "—";
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  return `${(ms / 1000).toFixed(2)}s`;
}
function formatUptime(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) {
    return "—";
  }
  return `${pct.toFixed(2)}%`;
}

/** A DB-only provider (custom Airside carrier) appended to the static grid.
 *  Carries its own model count since the static counts don't know it, plus the
 *  carrier's self-declared Airside profile for the card badges and filters. */
export interface ExtraGridProvider {
  id: string;
  name: string;
  description: string | null;
  modelsCount: number;
  headquarters: string | null;
  website: string | null;
  dataPolicy: ProviderDataPolicy | null;
}
interface ProvidersGridProps {
  /** When set, only providers headquartered in this ISO 3166-1 alpha-2 code are shown. */
  countryCode?: string;
  /** Overrides the default page heading. */
  heading?: string;
  /** Overrides the default page subheading. */
  subheading?: string;
  /** providerId → uploaded logo data URL (Airside carrier branding). */
  uploadedLogos?: Record<string, string>;
  /** DB-only providers (custom Airside carriers) to list alongside the
   *  static catalogue. */
  extraProviders?: ExtraGridProvider[];
  /** API-backed counts, including approved Airside listings. */
  modelCounts?: Record<string, number>;
}
type GridProvider = (typeof publicProviderDefinitions)[number] | ExtraGridProvider;
function modelsCountOf(p: GridProvider, modelCounts?: Record<string, number>): number {
  return modelCounts?.[p.id] ?? ("modelsCount" in p ? p.modelsCount : activeModelCounts[p.id] || 0);
}
export function ProvidersGrid({
  countryCode,
  heading,
  subheading,
  uploadedLogos,
  extraProviders,
  modelCounts
}: ProvidersGridProps = {}) {
  const router = useRouter();
  const api = useApi();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortKey>("popular");
  const [country, setCountry] = useState<string>("all");
  const [reqs, setReqs] = useState<Set<ComplianceFilterKey>>(new Set());
  const toggleReq = (key: ComplianceFilterKey) => {
    setReqs(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const visibleProviders = useMemo<GridProvider[]>(() => {
    const listedProviders = publicProviderDefinitions.filter(provider => modelsCountOf(provider, modelCounts) > 0);
    const all = [...listedProviders, ...(extraProviders ?? [])];
    if (countryCode) {
      return all.filter(p => p.headquarters === countryCode);
    }
    return all;
  }, [countryCode, extraProviders, modelCounts]);
  const totalProviders = visibleProviders.length;
  const totalModels = visibleProviders.reduce((sum, p_0) => sum + modelsCountOf(p_0, modelCounts), 0);
  const {
    data: statsData
  } = api.useQuery("get", "/public/providers/stats", {
    params: {
      query: {
        window: "7d" as const
      }
    }
  }, {
    refetchOnWindowFocus: false,
    staleTime: 5 * 60_000
  });
  const statsByProvider = useMemo(() => {
    const map = new Map<string, ProviderWindowStats>();
    if (statsData?.providers) {
      for (const row of statsData.providers) {
        map.set(row.providerId, gateProviderStats({
          logsCount: row.logsCount,
          uptime: row.uptime,
          avgTimeToFirstToken: row.avgTimeToFirstToken,
          timeToFirstTokenCount: row.timeToFirstTokenCount,
          throughput: row.throughput
        }));
      }
    }
    return map;
  }, [statsData]);
  const filteredAndSorted = useMemo(() => {
    const query = search.trim().toLowerCase();
    const enriched = visibleProviders.map(provider_0 => {
      const stats = statsByProvider.get(provider_0.id);
      return {
        ...provider_0,
        stats,
        modelsCount: modelsCountOf(provider_0, modelCounts)
      };
    });
    const compliancePolicy: ProviderCompliancePolicy = {
      enabled: true
    };
    reqs.forEach(key_0 => {
      compliancePolicy[key_0] = true;
    });
    const activeCountry = countryCode ?? (country === "all" ? undefined : country);
    if (activeCountry) {
      compliancePolicy.allowedCountries = [activeCountry];
    }
    const complianceFiltered = reqs.size > 0 || activeCountry ? enriched.filter(p_1 =>
    // Custom carriers are checked against their self-declared
    // profile; unstated attributes fail closed like the helper's
    // missing dataPolicy.
    isProviderCompliant(p_1 as Parameters<typeof isProviderCompliant>[0], compliancePolicy)) : enriched;
    const filtered = query ? complianceFiltered.filter(p_2 => p_2.name.toLowerCase().includes(query) || p_2.id.toLowerCase().includes(query) || (p_2.description?.toLowerCase().includes(query) ?? false)) : complianceFiltered;
    const sortValue = (n: number | null | undefined, fallback: number): number => n === null || n === undefined ? fallback : n;
    switch (sort) {
      case "fastest":
        return [...filtered].sort((a_3, b_3) => sortValue(a_3.stats?.avgTimeToFirstToken, Number.POSITIVE_INFINITY) - sortValue(b_3.stats?.avgTimeToFirstToken, Number.POSITIVE_INFINITY));
      case "slowest":
        return [...filtered].sort((a_2, b_2) => sortValue(b_2.stats?.avgTimeToFirstToken, -1) - sortValue(a_2.stats?.avgTimeToFirstToken, -1));
      case "uptime":
        return [...filtered].sort((a_1, b_1) => sortValue(b_1.stats?.uptime, -1) - sortValue(a_1.stats?.uptime, -1));
      case "name":
        return [...filtered].sort((a_0, b_0) => a_0.name.localeCompare(b_0.name));
      case "popular":
      default:
        return [...filtered].sort((a, b) => b.modelsCount - a.modelsCount);
    }
  }, [search, sort, statsByProvider, visibleProviders, country, modelCounts, reqs, countryCode]);
  return <div className="container mx-auto px-4 pt-60 pb-8">
			<header className="text-center mb-12">
				<h1 className="text-4xl font-bold tracking-tight mb-4">
					{heading ?? "AI Providers"}
				</h1>
				<p className="text-xl text-muted-foreground mb-6 max-w-3xl mx-auto">
					{subheading ?? `Access ${totalModels} models from ${totalProviders} leading AI providers through our unified API`}
				</p>
				<div className="flex justify-center gap-8 text-sm text-muted-foreground">
					<div className="flex items-center gap-2">
						<div className="w-2 h-2 bg-green-500 rounded-full" />
						<span>{totalProviders} Providers</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="w-2 h-2 bg-blue-500 rounded-full" />
						<span>{totalModels} Models</span>
					</div>
					{statsData?.window && <div className="flex items-center gap-2">
							<Zap className="h-3.5 w-3.5 text-amber-500" />
							<span>Stats from last {statsData.window}</span>
						</div>}
				</div>
			</header>

			<div className="mb-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="relative w-full sm:max-w-md">
					<Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
					<Input type="search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search providers by name or description" className="pl-9" />
				</div>
				<div className="flex items-center gap-2">
					<Button asChild variant="outline">
						<Link href="https://airside.llmgateway.io">
							<Plus className="h-4 w-4" />
							Add Provider
						</Link>
					</Button>
					<span className="text-sm text-muted-foreground hidden sm:inline">
						Sort by
					</span>
					<Select value={sort} onValueChange={v => setSort(v as SortKey)}>
						<SelectTrigger className="w-[180px]">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="popular">Most models</SelectItem>
							<SelectItem value="fastest">Fastest (TTFT)</SelectItem>
							<SelectItem value="slowest">Slowest (TTFT)</SelectItem>
							<SelectItem value="uptime">Highest uptime</SelectItem>
							<SelectItem value="name">Name (A–Z)</SelectItem>
						</SelectContent>
					</Select>
				</div>
			</div>

			<div className="mb-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
				{!countryCode && <div className="flex items-center gap-2">
						<span className="text-sm text-muted-foreground">Headquarters</span>
						<Select value={country} onValueChange={setCountry}>
							<SelectTrigger className="w-[190px]">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All countries</SelectItem>
								{PROVIDER_COUNTRIES.map(c => <SelectItem key={c.code} value={c.code}>
										{c.flag} {c.name}
									</SelectItem>)}
							</SelectContent>
						</Select>
					</div>}
				<div className="flex flex-wrap items-center gap-2">
					<span className="text-sm text-muted-foreground">Compliance</span>
					{COMPLIANCE_FILTERS.map(filter => {
          const active = reqs.has(filter.key);
          return <button key={filter.key} type="button" aria-pressed={active} onClick={() => toggleReq(filter.key)} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border/60 text-muted-foreground hover:bg-muted"}`}>
								<ShieldCheck className="h-3.5 w-3.5" />
								{filter.label}
							</button>;
        })}
					{(reqs.size > 0 || country !== "all") && <Button variant="ghost" size="sm" onClick={() => {
          setReqs(new Set());
          setCountry("all");
        }}>
							Clear
						</Button>}
				</div>
			</div>

			{filteredAndSorted.length === 0 ? <div className="rounded-xl border border-dashed py-16 text-center">
					<p className="text-muted-foreground">
						{search.trim() ? `No providers match "${search}"` : "No providers match the selected filters"}
						. Try adjusting your search or filters.
					</p>
				</div> : <div className="grid gap-4 sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{filteredAndSorted.map(provider_1 => {
        const {
          headquarters,
          dataPolicy,
          website
        } = provider_1;
        return <Card key={provider_1.id} className="group relative flex h-full cursor-pointer flex-col overflow-hidden border-border/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5" onClick={() => router.push(`/providers/${provider_1.id}`)}>
								<div aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-primary/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
								<CardHeader className="flex flex-1 flex-col gap-4">
									<div className="flex items-start justify-between gap-3">
										{getProviderLogo(provider_1.id as ProviderId, uploadedLogos?.[provider_1.id])}
										<span className="inline-flex shrink-0 items-center gap-1 text-sm text-muted-foreground transition-colors group-hover:text-primary">
											View models
											<ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
										</span>
									</div>

									<div className="space-y-2">
										<div className="flex flex-wrap items-center gap-2">
											<CardTitle className="text-xl">{provider_1.name}</CardTitle>
										</div>
										<CardDescription className="line-clamp-2 leading-relaxed">
											{provider_1.description}
										</CardDescription>
									</div>

									{provider_1.stats && !hasEnoughRequestsForStats(provider_1.stats.logsCount) && <div className="rounded-xl border border-dashed border-border/60 bg-muted/20 p-3 text-xs text-muted-foreground">
												Stats hidden until{" "}
												{formatCompact(MIN_REQUESTS_FOR_STATS)} requests
											</div>}

									{provider_1.stats && (provider_1.stats.avgTimeToFirstToken !== null || provider_1.stats.uptime !== null) && <div className="grid grid-cols-2 divide-x divide-border/60 overflow-hidden rounded-xl border border-border/60 bg-muted/30">
												<div className="flex items-center gap-2.5 p-3">
													<Gauge className="h-4 w-4 shrink-0 text-muted-foreground/70" />
													<div className="min-w-0">
														<div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
															TTFT
														</div>
														<div className="font-mono text-sm font-semibold tabular-nums">
															{formatTtft(provider_1.stats.avgTimeToFirstToken)}
														</div>
													</div>
												</div>
												<div className="flex items-center gap-2.5 p-3">
													<Activity className="h-4 w-4 shrink-0 text-muted-foreground/70" />
													<div className="min-w-0">
														<div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
															Uptime
														</div>
														<div className="font-mono text-sm font-semibold tabular-nums">
															{formatUptime(provider_1.stats.uptime)}
														</div>
													</div>
												</div>
											</div>}

									<div className="mt-auto flex items-end justify-between gap-3 border-t border-border/60 pt-4">
										<div className="flex flex-col">
											<span className="text-lg font-semibold leading-none tabular-nums">
												{provider_1.modelsCount}
											</span>
											<span className="mt-1 text-xs text-muted-foreground">
												{provider_1.modelsCount === 1 ? "model" : "models"}
											</span>
										</div>
										<div className="flex flex-wrap items-center justify-end gap-1.5">
											{headquarters && <CountryBadge code={headquarters} />}
											{dataPolicy?.apiTraining === false && <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-600 dark:text-emerald-400">
													<ShieldCheck className="h-3 w-3" />
													No training
												</span>}
											{website && <a href={website} target="_blank" rel="noopener noreferrer" onClick={e_0 => e_0.stopPropagation()} className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-border/60 bg-muted/30 px-2 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary">
													<ExternalLink className="h-3 w-3" />
													Website
												</a>}
										</div>
									</div>
								</CardHeader>
							</Card>;
      })}
				</div>}
		</div>;
}